import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip
} from '@mui/material';
import { useTheme, styled } from '@mui/material/styles';
import {
  FileDownload,
  Receipt,
  AttachMoney,
  Percent,
  ReceiptLong,
  Hotel as HotelIcon,
  CheckCircle,
  Replay
} from '@mui/icons-material';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import baseAxios, { CancelTokenSource } from 'axios';
import axios from '../../resources/axios';
import Title from '../../components/Title';
import { selectAuth } from '../../redux/slices/authSlice';
import { selectAdmins } from '../../redux/slices/adminsSlice';
import {
  getManagementCompaniesAsync,
  selectManagementCompanies
} from '../../redux/slices/managementCompaniesSlice';
import { ManagementCompany } from '../../resources/types/managementCompaniesTypes';
import { ParkingContractStatRow } from '../../resources/types/managementCompaniesTypes';
import * as XLSX from 'xlsx';

// Backend rejects ranges longer than 90 days and requires a companyId,
// so the report is assembled from per-company, per-chunk requests.
const MAX_PERIOD_DAYS = 90;
const MAX_PARALLEL_REQUESTS = 3;
const MAX_ATTEMPTS = 3;

const splitPeriod = (from: string, to: string) => {
  const chunks: { dateFrom: string; dateTo: string }[] = [];
  const end = moment(to);
  let start = moment(from);
  while (!start.isAfter(end)) {
    const chunkEnd = moment.min(start.clone().add(MAX_PERIOD_DAYS - 1, 'days'), end);
    chunks.push({
      dateFrom: start.format('YYYY-MM-DD'),
      dateTo: chunkEnd.format('YYYY-MM-DD')
    });
    start = chunkEnd.clone().add(1, 'day');
  }
  return chunks;
};

// Large chunks occasionally hit a gateway timeout (504); retry server and
// network errors, but fail fast on 4xx.
const withRetry = async <T,>(
  fn: () => Promise<T>,
  isCancelled: () => boolean
): Promise<T> => {
  for (let attempt = 1; ; attempt++) {
    try {
      return await fn();
    } catch (e: any) {
      const status = e?.response?.status;
      const retryable = !baseAxios.isCancel(e) && (!status || status >= 500);
      if (!retryable || attempt >= MAX_ATTEMPTS || isCancelled()) throw e;
      await new Promise(r => setTimeout(r, 1000 * attempt));
    }
  }
};

// Stops handing out tasks once any task fails or the run is cancelled.
const runLimited = async <T,>(
  tasks: (() => Promise<T>)[],
  limit: number,
  isCancelled: () => boolean
) => {
  const results: T[] = new Array(tasks.length);
  let next = 0;
  let failed = false;
  const worker = async () => {
    while (next < tasks.length && !failed && !isCancelled()) {
      const i = next++;
      try {
        results[i] = await tasks[i]();
      } catch (e) {
        failed = true;
        throw e;
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker));
  return results;
};

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor:
      theme.palette.mode === 'light'
        ? 'rgba(78,205,196,0.04)'
        : 'rgba(255,255,255,0.02)'
  },
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'light'
        ? 'rgba(78,205,196,0.12)'
        : 'rgba(78,205,196,0.08)'
  }
}));

const TableHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 700,
  fontSize: 13,
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
  backgroundColor:
    theme.palette.mode === 'light' ? '#F5FBFA' : 'rgba(255,255,255,0.03)'
}));

const StatCard = ({
  icon,
  label,
  value,
  accent
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  accent: string;
}) => {
  const theme = useTheme();
  return (
    <Card
      sx={{
        borderRadius: 2,
        border: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : 'none',
        background: `linear-gradient(135deg, ${accent}15 0%, ${accent}05 100%)`,
        height: '100%'
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              bgcolor: `${accent}25`,
              color: accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary, display: 'block' }}
            >
              {label}
            </Typography>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: accent, lineHeight: 1.2 }}
            >
              {value}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const HotelReportPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const auth = useSelector(selectAuth);
  const admin = useSelector(selectAdmins);
  const mcState = useSelector(selectManagementCompanies);

  const isSuperAdmin = auth.info?.type === 'superAdmin';
  const endpoint = isSuperAdmin
    ? '/management_companies/parking_contracts_stats'
    : '/management_companies/parking_contracts_stats_view';

  const allowedCompanies = useMemo<{ _id: string; name: string }[]>(() => {
    if (isSuperAdmin) {
      return mcState.companies.map((c: ManagementCompany) => ({
        _id: c._id,
        name: c.name
      }));
    }
    return (admin.info?.managementCompanies || []).map((c: ManagementCompany | string) => {
      if (typeof c !== 'string') return { _id: c._id, name: c.name };
      const found = mcState.companies.find((mc: ManagementCompany) => mc._id === c);
      return { _id: c, name: found?.name || c };
    });
  }, [isSuperAdmin, mcState.companies, admin.info]);

  const [company, setCompany] = useState<string>('');
  const [hotel, setHotel] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>(
    moment().startOf('month').format('YYYY-MM-DD')
  );
  // Default to the current month up to today to keep the initial load small.
  const [dateTo, setDateTo] = useState<string>(moment().format('YYYY-MM-DD'));
  const [rows, setRows] = useState<ParkingContractStatRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<'json' | 'excel' | null>(null);

  useEffect(() => {
    if (isSuperAdmin) {
      dispatch(getManagementCompaniesAsync(true));
    }
  }, [isSuperAdmin, dispatch]);

  // Auto-select the only allowed company for restricted hotel admins.
  useEffect(() => {
    if (!isSuperAdmin && !company && allowedCompanies.length === 1) {
      setCompany(allowedCompanies[0]._id);
    }
  }, [isSuperAdmin, company, allowedCompanies]);

  // Restricted hotel admins must pick a company (the view endpoint requires it).
  const needsCompany = !isSuperAdmin && !company;

  const requestIdRef = useRef(0);
  const cancelSourceRef = useRef<CancelTokenSource | null>(null);

  // Cancel in-flight report requests when the page unmounts.
  useEffect(
    () => () => {
      requestIdRef.current++;
      cancelSourceRef.current?.cancel();
    },
    []
  );

  const fetchReport = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    cancelSourceRef.current?.cancel();
    cancelSourceRef.current = null;
    if (!dateFrom || !dateTo) {
      setError(t('hotelReport.errorDates'));
      setLoading(false);
      return;
    }
    if (moment(dateFrom).isAfter(moment(dateTo))) {
      setError(t('hotelReport.errorOrder'));
      setLoading(false);
      return;
    }
    if (!isSuperAdmin && !company) {
      setRows([]);
      setLoading(false);
      return;
    }
    const companyIds = company ? [company] : allowedCompanies.map(c => c._id);
    if (companyIds.length === 0) {
      setRows([]);
      setLoading(false);
      return;
    }
    const cancelSource = baseAxios.CancelToken.source();
    cancelSourceRef.current = cancelSource;
    const isCancelled = () => requestId !== requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const tasks = companyIds.flatMap(companyId =>
        splitPeriod(dateFrom, dateTo).map(period => () =>
          withRetry(async () => {
            const response = await axios.get(endpoint, {
              params: { ...period, companyId, format: 'json' },
              cancelToken: cancelSource.token
            });
            return (response.data?.data || []) as ParkingContractStatRow[];
          }, isCancelled)
        )
      );
      const chunks = await runLimited(tasks, MAX_PARALLEL_REQUESTS, isCancelled);
      if (isCancelled()) return;
      const merged = chunks
        .flat()
        .sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf());
      setRows(merged);
    } catch (e) {
      // Abort the sibling requests still in flight for this run.
      cancelSource.cancel();
      if (isCancelled()) return;
      setError(t('hotelReport.loadError'));
      setRows([]);
    } finally {
      if (cancelSourceRef.current === cancelSource) cancelSourceRef.current = null;
      if (!isCancelled()) setLoading(false);
    }
  }, [dateFrom, dateTo, company, endpoint, isSuperAdmin, allowedCompanies, t]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Available hotels are derived from the loaded rows (no separate API param).
  const hotelOptions = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => {
      const name = r.hotelNameAtStart || r.parkingName;
      if (name) set.add(name);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  // Reset hotel filter when it no longer exists in the current dataset.
  useEffect(() => {
    if (hotel && !hotelOptions.includes(hotel)) {
      setHotel('');
    }
  }, [hotel, hotelOptions]);

  const filteredRows = useMemo(() => {
    if (!hotel) return rows;
    return rows.filter(r => (r.hotelNameAtStart || r.parkingName) === hotel);
  }, [rows, hotel]);

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, r) => ({
        trips: acc.trips + (r.refunded ? 0 : 1),
        refunded: acc.refunded + (r.refunded ? 1 : 0),
        withIva: acc.withIva + (r.amountWithIva || 0),
        exIva: acc.exIva + (r.amountExIva || 0),
        iva: acc.iva + (r.iva21Amount || 0),
        commission: acc.commission + (r.commissionAmount || 0)
      }),
      { trips: 0, refunded: 0, withIva: 0, exIva: 0, iva: 0, commission: 0 }
    );
  }, [filteredRows]);

  const fmtMoney = (v: number) => `€${(v || 0).toFixed(2)}`;

  const handleDownload = async (format: 'json' | 'excel') => {
    if (!dateFrom || !dateTo || (!isSuperAdmin && !company)) return;

    // Build the Excel client-side from the displayed rows so it matches the
    // on-screen table (including a totals row at the bottom).
    if (format === 'excel') {
      setDownloading('excel');
      try {
        const sheetData: Record<string, string | number>[] = filteredRows.map(
          r => ({
            [t('hotelReport.tableDate')]: moment(r.createdAt).format(
              'DD.MM.YYYY HH:mm'
            ),
            [t('hotelReport.tableHotel')]:
              r.hotelNameAtStart || r.parkingName || '',
            [t('hotelReport.tableClient')]: r.riderName || '',
            [t('hotelReport.tableStatus')]:
              r.refunded || !r.amountWithIva
                ? t('hotelReport.statusRefunded')
                : t('hotelReport.statusOk'),
            [t('hotelReport.tableWithIva')]: r.amountWithIva || 0,
            [t('hotelReport.tableExIva')]: r.amountExIva || 0,
            [t('hotelReport.tableIva')]: r.iva21Amount || 0,
            [t('hotelReport.tableCommissionPercent')]: r.commissionPercent || 0,
            [t('hotelReport.tableCommission')]: r.commissionAmount || 0
          })
        );

        sheetData.push({
          [t('hotelReport.tableDate')]: t('hotelReport.totals'),
          [t('hotelReport.tableHotel')]: '',
          [t('hotelReport.tableClient')]: '',
          [t('hotelReport.tableStatus')]: '',
          [t('hotelReport.tableWithIva')]: totals.withIva,
          [t('hotelReport.tableExIva')]: totals.exIva,
          [t('hotelReport.tableIva')]: totals.iva,
          [t('hotelReport.tableCommissionPercent')]: '',
          [t('hotelReport.tableCommission')]: totals.commission
        });

        const worksheet = XLSX.utils.json_to_sheet(sheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Trips');

        const companyLabel = company
          ? allowedCompanies.find(c => c._id === company)?.name || 'company'
          : 'all_companies';
        const safeLabel = (hotel || companyLabel).replace(/[^\w]+/g, '_');
        XLSX.writeFile(
          workbook,
          `hotel_trips_${safeLabel}_${dateFrom}_${dateTo}.xlsx`
        );
      } catch (e) {
        window.alert(t('hotelReport.downloadError'));
      } finally {
        setDownloading(null);
      }
      return;
    }

    setDownloading(format);
    try {
      const params: any = { dateFrom, dateTo, format };
      if (company) params.companyId = company;
      const response = await axios.get(endpoint, {
        params,
        responseType: format === 'excel' ? 'blob' : 'json'
      });

      const companyLabel = company
        ? allowedCompanies.find(c => c._id === company)?.name || 'company'
        : 'all_companies';
      const safeLabel = companyLabel.replace(/[^\w]+/g, '_');
      const filename = `hotel_trips_${safeLabel}_${dateFrom}_${dateTo}.${
        format === 'excel' ? 'xlsx' : 'json'
      }`;

      const blob =
        format === 'excel'
          ? (response.data as Blob)
          : new Blob([JSON.stringify(response.data, null, 2)], {
              type: 'application/json'
            });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      window.alert(t('hotelReport.downloadError'));
    } finally {
      setDownloading(null);
    }
  };

  const downloadDisabled =
    downloading !== null || !dateFrom || !dateTo || needsCompany;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <HotelIcon sx={{ color: '#4ECDC4', fontSize: 30 }} />
        <Title>{t('hotelReport.title')}</Title>
      </Box>
      <Typography
        variant="body2"
        sx={{ mb: 2.5, color: theme.palette.text.secondary }}
      >
        {t('hotelReport.subtitle')}
      </Typography>

      <Paper
        sx={{
          p: 2,
          mb: 2.5,
          borderRadius: 2,
          border: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : 'none'
        }}
      >
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              size="small"
              label={t('hotelReport.company')}
              value={company}
              onChange={e => setCompany(e.target.value)}
              error={needsCompany}
              helperText={needsCompany ? t('hotelReport.selectCompanyPrompt') : ' '}
            >
              {isSuperAdmin && (
                <MenuItem value="">{t('hotelReport.allCompanies')}</MenuItem>
              )}
              {allowedCompanies.map(c => (
                <MenuItem key={c._id} value={c._id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              size="small"
              label={t('hotelReport.hotel')}
              value={hotel}
              onChange={e => setHotel(e.target.value)}
              disabled={hotelOptions.length === 0}
              helperText=" "
            >
              <MenuItem value="">{t('hotelReport.allHotels')}</MenuItem>
              {hotelOptions.map(h => (
                <MenuItem key={h} value={h}>
                  {h}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={t('hotelReport.dateFrom')}
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText=" "
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={t('hotelReport.dateTo')}
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText=" "
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title={t('hotelReport.excel')}>
                <span style={{ flex: 1 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    disabled={downloadDisabled}
                    onClick={() => handleDownload('excel')}
                    startIcon={
                      downloading === 'excel' ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <FileDownload />
                      )
                    }
                    sx={{ bgcolor: '#4ECDC4', '&:hover': { bgcolor: '#3BA99F' } }}
                  >
                    {t('hotelReport.excel')}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
        {error && (
          <Box
            sx={{
              mt: 1.5,
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'rgba(244, 67, 54, 0.08)',
              color: '#F44336',
              fontSize: 14
            }}
          >
            {error}
          </Box>
        )}
      </Paper>

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard
            icon={<Receipt />}
            label={t('hotelReport.trips')}
            value={totals.trips}
            accent="#4ECDC4"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard
            icon={<AttachMoney />}
            label={t('hotelReport.totalWithIva')}
            value={fmtMoney(totals.withIva)}
            accent="#3BA99F"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard
            icon={<ReceiptLong />}
            label={t('hotelReport.totalExIva')}
            value={fmtMoney(totals.exIva)}
            accent="#4D96FF"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard
            icon={<Percent />}
            label={t('hotelReport.totalIva')}
            value={fmtMoney(totals.iva)}
            accent="#FFB400"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard
            icon={<Percent />}
            label={t('hotelReport.commission')}
            value={fmtMoney(totals.commission)}
            accent="#9B5DE5"
          />
        </Grid>
      </Grid>

      <Paper
        sx={{
          borderRadius: 2,
          border: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : 'none',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 2,
            pb: 1
          }}
        >
          <ReceiptLong sx={{ color: '#4ECDC4' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('hotelReport.tableTitle')}
          </Typography>
          {loading && (
            <CircularProgress size={18} sx={{ color: '#4ECDC4', ml: 1 }} />
          )}
        </Box>

        {needsCompany ? (
          <Box
            sx={{ py: 6, textAlign: 'center', color: theme.palette.text.secondary }}
          >
            <HotelIcon sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
            <Typography>{t('hotelReport.selectCompanyPrompt')}</Typography>
          </Box>
        ) : filteredRows.length === 0 && !loading ? (
          <Box
            sx={{ py: 6, textAlign: 'center', color: theme.palette.text.secondary }}
          >
            <Typography>{t('hotelReport.noData')}</Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t('hotelReport.tableDate')}</TableHeaderCell>
                  <TableHeaderCell>{t('hotelReport.tableHotel')}</TableHeaderCell>
                  <TableHeaderCell>{t('hotelReport.tableClient')}</TableHeaderCell>
                  <TableHeaderCell>{t('hotelReport.tableStatus')}</TableHeaderCell>
                  <TableHeaderCell align="right">
                    {t('hotelReport.tableWithIva')}
                  </TableHeaderCell>
                  <TableHeaderCell align="right">
                    {t('hotelReport.tableExIva')}
                  </TableHeaderCell>
                  <TableHeaderCell align="right">
                    {t('hotelReport.tableIva')}
                  </TableHeaderCell>
                  <TableHeaderCell align="right">
                    {t('hotelReport.tableCommissionPercent')}
                  </TableHeaderCell>
                  <TableHeaderCell align="right">
                    {t('hotelReport.tableCommission')}
                  </TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((r, i) => (
                  <StyledTableRow key={i}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {moment(r.createdAt).format('DD.MM.YYYY HH:mm')}
                    </TableCell>
                    <TableCell>
                      {r.hotelNameAtStart || r.parkingName || '—'}
                    </TableCell>
                    <TableCell>{r.riderName || '—'}</TableCell>
                    <TableCell>
                      {r.refunded || !r.amountWithIva ? (
                        <Chip
                          size="small"
                          icon={<Replay sx={{ fontSize: 16, color: '#9E9E9E' }} />}
                          label={t('hotelReport.statusRefunded')}
                          sx={{
                            bgcolor: 'rgba(158,158,158,0.15)',
                            color: '#757575',
                            fontWeight: 600
                          }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          icon={<CheckCircle sx={{ fontSize: 16 }} />}
                          label={t('hotelReport.statusOk')}
                          sx={{
                            bgcolor: 'rgba(78,205,196,0.15)',
                            color: '#3BA99F',
                            fontWeight: 600
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {fmtMoney(r.amountWithIva)}
                    </TableCell>
                    <TableCell align="right">{fmtMoney(r.amountExIva)}</TableCell>
                    <TableCell align="right">{fmtMoney(r.iva21Amount)}</TableCell>
                    <TableCell align="right">{r.commissionPercent}%</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {fmtMoney(r.commissionAmount)}
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
              {filteredRows.length > 0 && (
                <TableBody>
                  <TableRow
                    sx={{
                      bgcolor:
                        theme.palette.mode === 'light'
                          ? 'rgba(78,205,196,0.10)'
                          : 'rgba(78,205,196,0.06)'
                    }}
                  >
                    <TableCell sx={{ fontWeight: 700 }} colSpan={4}>
                      {t('hotelReport.totals')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {fmtMoney(totals.withIva)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {fmtMoney(totals.exIva)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {fmtMoney(totals.iva)}
                    </TableCell>
                    <TableCell />
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {fmtMoney(totals.commission)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default HotelReportPage;
